import axios from "./index";

// フェイスシート関連のCRUD APIクライアント。
// バックエンド: app/controllers/api/v1/face_sheets_controller.rb ほか。

const BASE = "/api/v1/face_sheets";

export type SavePayloadBody = Record<string, unknown>;

// 「ハイリスク要対応」「要注意観察中」は独立したフラグ（同時にON/OFFが可能）。
export type FaceSheetStatusFlags = {
  actionRequired: boolean;
  watching: boolean;
};

// フェイスシートTOPの検索条件（サーバー側でDB検索するためのクエリパラメータ）。
export type FaceSheetSearchParams = {
  user_id?: string;
  family_name_kana?: string;
  first_name_kana?: string;
  postal_code?: string;
  coupon?: string;
  birthday?: string;
  child_birthday?: string;
  statuses?: string[];
  unhandled_alerts?: string[];
  history_conditions?: string[];
};

// 楽観的ロック用に、body に lockVersion があれば snake_case の lock_version を付与する。
// 新規作成時など lockVersion が無い場合はそのまま返す。
const withLockVersion = (body: SavePayloadBody): SavePayloadBody => {
  if (body.lockVersion === undefined || body.lockVersion === null) {
    return body;
  }
  return { ...body, lock_version: body.lockVersion };
};

export default {
  fetchFaceSheets(params?: FaceSheetSearchParams) {
    return axios.get(`${BASE}`, { params }).then((response) => ({
      items: response.data.items,
      capped: response.data.capped === true
    }));
  },
  fetchFaceSheetDetail(userId: string) {
    return axios.get(`${BASE}/${userId}`).then((response) => response.data);
  },
  updateStatus(userId: string, statusFlags: FaceSheetStatusFlags, lockVersion: number) {
    return axios.patch(`${BASE}/${userId}/status`, {
      action_required: statusFlags.actionRequired,
      watching: statusFlags.watching,
      lock_version: lockVersion
    }).then((response) => response.data.item);
  },
  saveSpecialNote(userId: string, specialNote: string, baseRevisionId: string | null) {
    return axios.put(`${BASE}/${userId}/special_note`, {
      special_note: specialNote,
      base_revision_id: baseRevisionId
    }).then((response) => response.data.item);
  },
  createExternalLink(userId: string, body: SavePayloadBody) {
    return axios.post(`${BASE}/${userId}/external_links`, { external_link: withLockVersion(body) }).then((response) => response.data.item);
  },
  updateExternalLink(userId: string, id: string, body: SavePayloadBody) {
    return axios.put(`${BASE}/${userId}/external_links/${id}`, { external_link: withLockVersion(body) }).then((response) => response.data.item);
  },
  deleteExternalLink(userId: string, id: string) {
    return axios.delete(`${BASE}/${userId}/external_links/${id}`).then((response) => response.data);
  },
  createFrequentSupport(userId: string, body: SavePayloadBody) {
    return axios.post(`${BASE}/${userId}/frequent_supports`, { frequent_support: withLockVersion(body) }).then((response) => response.data.item);
  },
  updateFrequentSupport(userId: string, id: string, body: SavePayloadBody) {
    return axios.put(`${BASE}/${userId}/frequent_supports/${id}`, { frequent_support: withLockVersion(body) }).then((response) => response.data.item);
  },
  deleteFrequentSupport(userId: string, id: string) {
    return axios.delete(`${BASE}/${userId}/frequent_supports/${id}`).then((response) => response.data);
  },
  updateAlertSupport(userId: string, id: string, body: SavePayloadBody) {
    return axios.put(`${BASE}/${userId}/alert_supports/${id}`, { alert_support: withLockVersion(body) }).then((response) => response.data.item);
  },
  createMunicipalCooperation(userId: string, body: SavePayloadBody) {
    return axios.post(`${BASE}/${userId}/municipal_cooperations`, { municipal_cooperation: withLockVersion(body) }).then((response) => response.data.item);
  },
  updateMunicipalCooperation(userId: string, id: string, body: SavePayloadBody) {
    return axios.put(`${BASE}/${userId}/municipal_cooperations/${id}`, { municipal_cooperation: withLockVersion(body) }).then((response) => response.data.item);
  },
  deleteMunicipalCooperation(userId: string, id: string) {
    return axios.delete(`${BASE}/${userId}/municipal_cooperations/${id}`).then((response) => response.data);
  }
};
